import React, { useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { TypePill } from "./TypePill";
import { getItemTypeColor } from "../utils/colors";
import { ConnectionRow } from "./Connection/ConnectionRow";
import { Link } from "react-router-dom";
import { useAppSelector } from "../store/hooks";
import { selectEnrichedConnectionsForListing } from "../store/slices/listingsSlice";
import { getLabel } from "../strings/labels";
import {
  ConnectionRoleType,
  ListingWithDetails,
  EnrichedConnection,
} from "../types";

interface ItemDetailViewProps {
  item: ListingWithDetails;
  onConnectionClick?: (item: ListingWithDetails) => void;
}

export const ItemDetailView: React.FC<ItemDetailViewProps> = ({
  item,
  onConnectionClick,
}) => {
  // HOOKS
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get enriched connections using selector
  const enrichedConnections = useAppSelector((state) =>
    selectEnrichedConnectionsForListing(state, item.id)
  );

  // VARS
  const { itemType } = item;
  const creatorLabel = getLabel(itemType, "creator", true);
  const collaboratorLabel = getLabel(itemType, "collaborator", true);
  const participantLabel = getLabel(itemType, "participant", true);

  const handleBack = () => {
    const params = new URLSearchParams(searchParams);
    params.delete("selected");
    navigate({ search: params.toString() }, { replace: false });
  };

  // Determine item type label
  const typeLabel =
    item.itemType === "person" ? "person" : item.subType || item.itemType;
  const typeColor = getItemTypeColor(item.itemType);

  // Get the back button label
  const getBackLabel = () => {
    const tab = searchParams.get("tab");
    switch (tab) {
      case "person":
        return "back to members";
      case "event":
        return "back to events";
      case "product":
        return "back to products";
      case "project":
        return "back to projects";
      case "service":
        return "back to services";
      default:
        return "back";
    }
  };

  // Filter enriched connections by role type
  const { collaborator, creator, participant } = useMemo(() => {
    const grouped: Record<ConnectionRoleType, EnrichedConnection[]> = {
      collaborator: [],
      creator: [],
      participant: [],
      listed: [],
    };
    enrichedConnections.forEach((ec) => {
      const roleType = ec.connection.roleType;
      if (grouped[roleType]) {
        grouped[roleType].push(ec);
      }
    });
    return grouped;
  }, [enrichedConnections]);

  const hasCreators = creator && creator.length > 0;
  const hasCollaborators = collaborator && collaborator.length > 0;
  const hasParticipants = participant && participant.length > 0;

  return (
    <div className="item-detail-view">
      {/* Back button */}
      <button onClick={handleBack} className="detail-back-button">
        ← {getBackLabel()}
      </button>

      {/* Image */}
      {item.imageUrl && (
        <div className="detail-image-container">
          <img src={item.imageUrl} alt={item.name} className="detail-image" />
        </div>
      )}

      {/* Title and meta */}
      <div className="detail-header-section">
        <h2 className="detail-item-title">{item.name}</h2>
        <div className="detail-meta-row">
          <TypePill label={typeLabel} color={typeColor} />
          {item.locationName && (
            <span className="detail-location-text">📍 {item.locationName}</span>
          )}
        </div>
      </div>

      {/* Description */}
      {item.description && (
        <div className="detail-section">
          <h3 className="detail-section-title">About</h3>
          <p className="detail-description-text">{item.description}</p>
        </div>
      )}

      {/* Actions */}

      <div className="detail-section">
        <Link
          to={`/${item.itemType}s/${item.id}`}
          className="detail-primary-button"
          onClick={(e) => e.stopPropagation()}
        >
          View {item.itemType} →
        </Link>
      </div>

      {item.url && (
        <div className="detail-section">
          <a
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            className="card-link"
          >
            🔗 Visit external Website →
          </a>
        </div>
      )}

      {/* Connections */}
      {enrichedConnections && enrichedConnections.length > 0 && (
        <div className="detail-section">
          <div className="detail-connections">
            {/* Creators */}
            {hasCreators && (
              <div className="connection-group">
                <h4 className="connection-group-title">{creatorLabel}</h4>
                {creator.map((ec: EnrichedConnection, index) => {
                  return (
                    <ConnectionRow
                      enrichedConnection={ec}
                      key={`${ec.listing.id}-${ec.connection.roleType || "none"}-${index}`}
                      onClick={onConnectionClick}
                    />
                  );
                })}
              </div>
            )}
            {/* Collaborators */}
            {hasCollaborators && (
              <div className="connection-group">
                <h4 className="connection-group-title">{collaboratorLabel}</h4>
                {collaborator.map((ec: EnrichedConnection, index) => {
                  return (
                    <ConnectionRow
                      enrichedConnection={ec}
                      key={`${ec.listing.id}-${ec.connection.roleType || "none"}-${index}`}
                      onClick={onConnectionClick}
                    />
                  );
                })}
              </div>
            )}
            {/* Participants */}
            {hasParticipants && (
              <div className="connection-group">
                <h4 className="connection-group-title">{participantLabel}</h4>
                {participant.map((ec: EnrichedConnection, index) => {
                  return (
                    <ConnectionRow
                      enrichedConnection={ec}
                      key={`${ec.listing.id}-${ec.connection.roleType || "none"}-${index}`}
                      onClick={onConnectionClick}
                    />
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
